import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import { Translation } from 'cng-common/features/translations'
import { IColumn } from 'core-components/library/list/types'

import {
  TMarketYoyGrowthExpectationMarket,
  TMarketYoyGrowthExpectationRtm,
} from '../../../api/types'
import { MARKET_YOY_GROWTH_EXPECTATION_TRANSLATION_KEYS } from '../../../configs/translations'
import { IMarketYoyGrowthEditTarget } from '../../../lib'
import { GrowthValueInput } from '../components/growthValueInput/GrowthValueInput'
import { IGrowthValues, TGrowthField, TMarketYoyGrowthExpectationTableRow } from '../types'

import { renderGrowthValue } from './renderGrowthValue'

const KEYS = MARKET_YOY_GROWTH_EXPECTATION_TRANSLATION_KEYS

interface ICreateGrowthColumnParams {
  field: string
  parentField: string
  title: string
  expandedOfferings: Set<ProgramTypes>
  getValues: (row: TMarketYoyGrowthExpectationTableRow) => IGrowthValues | undefined
  growthField: TGrowthField
  isEditable: boolean
  /** Market the column belongs to; `null` means the Worldwide aggregate. */
  market: TMarketYoyGrowthExpectationMarket | null
  /** The currently viewed RTM — committed edits are scoped to it. */
  rtm: TMarketYoyGrowthExpectationRtm
  onGrowthValueCommit: (target: IMarketYoyGrowthEditTarget) => void
}

export const createGrowthColumn = ({
  field,
  parentField,
  title,
  expandedOfferings,
  getValues,
  growthField,
  isEditable,
  market,
  rtm,
  onGrowthValueCommit,
}: ICreateGrowthColumnParams): IColumn<TMarketYoyGrowthExpectationTableRow> => ({
  field,
  parentField,
  title,
  disableSort: true,
  hideSettings: true,
  titleRender: () => <Translation text={title} />,
  renderFunction: (row) => {
    if (row.type === 'group' && expandedOfferings.has(row.offering)) {
      return <></>
    }

    const value = getValues(row)?.[growthField] ?? null

    if (isEditable) {
      return (
        <GrowthValueInput
          value={value}
          onCommit={(nextValue) =>
            onGrowthValueCommit({
              market,
              offering: row.offering,
              // A group-row cell targets every category of the offering.
              productCategory: row.type === 'item' ? row.productCategory : null,
              rtm,
              growthField,
              value: nextValue,
            })
          }
        />
      )
    }

    return renderGrowthValue(value)
  },
})

export const createGrowthColumnPair = (
  params: Omit<ICreateGrowthColumnParams, 'field' | 'title' | 'growthField'> & {
    fieldPrefix?: string
  }
): IColumn<TMarketYoyGrowthExpectationTableRow>[] => {
  const { fieldPrefix = '', ...rest } = params

  return [
    createGrowthColumn({
      ...rest,
      field: `${fieldPrefix}yoyGrowth`,
      title: KEYS.table.yoyGrowth,
      growthField: 'yoyGrowth',
    }),
    createGrowthColumn({
      ...rest,
      field: `${fieldPrefix}sobYoyGrowth`,
      title: KEYS.table.sobYoyGrowth,
      growthField: 'sobYoyGrowth',
    }),
  ]
}
